/**
 * Security Filters Hook using React Query
 * 
 * Handles security filters data fetching with React Query
 */

import { useQuery } from '@tanstack/react-query';
import { 
  getSecurityFilters,
} from '@/services/security-scanner';

/**
 * Query key factory for security filters
 */
export const securityFiltersKeys = {
  all: ['securityFilters'] as const,
  detail: (orgId: string | number, userId: string | number) => 
    [...securityFiltersKeys.all, 'detail', orgId, userId] as const,
};

/**
 * Hook to fetch security filters data
 * 
 * @example
 * const { data, isLoading, error } = useSecurityFilters({
 *   org_id: 19,
 *   user_id: 14
 * });
 * // data.severities - Array of severity strings
 * // data.categories - Array of category strings
 */
export function useSecurityFilters(params?: {
  org_id?: number | string;
  user_id?: number | string;
}) {
  return useQuery({
    queryKey: securityFiltersKeys.detail(
      params?.org_id || '',
      params?.user_id || ''
    ),
    queryFn: () => getSecurityFilters({
      org_id: params!.org_id!,
      user_id: params!.user_id!,
    }),
    enabled: !!params?.org_id && !!params?.user_id, // Only fetch if both IDs are provided
    staleTime: 1000 * 60 * 10, // 10 minutes - filters don't change often
  });
}
